/**
 * Contact Service
 *
 * API service for contact CRUD operations.
 * Connects to FastAPI /api/contacts endpoints.
 */

import { apiClient } from '@shared/services/apiClient';
import { apiCall } from '@shared/utils/apiWrapper';
import type {
  ContactListResponse,
  ContactItem,
  ContactCreateRequest,
  ContactUpdateRequest,
  ContactListParams,
  SuccessResponse,
} from '@shared/types/api';
import type { OrganizationTreeNode } from '@features/contacts/types/contact.types';

/**
 * Batch update response
 */
interface BatchUpdateResponse {
  updated_count: number;
  failed_count: number;
}

/**
 * Fetch paginated list of contacts
 *
 * @param params - Query parameters for filtering, sorting, and pagination
 * @returns Promise resolving to contact list with metadata
 *
 * @example
 * ```typescript
 * const result = await fetchContacts({
 *   page: 1,
 *   per_page: 20,
 *   search: 'john',
 *   organization_id: 123
 * });
 * ```
 */
export async function fetchContacts(
  params: ContactListParams = {}
): Promise<ContactListResponse> {
  return apiCall('fetch contacts', () =>
    apiClient.get<ContactListResponse>('/contacts', { params })
  );
}

/**
 * Fetch single contact by ID
 *
 * @param contactId - Contact ID
 * @returns Promise resolving to contact details
 *
 * @example
 * ```typescript
 * const contact = await fetchContactById(456);
 * console.log(contact.name); // "John Doe"
 * ```
 */
export async function fetchContactById(contactId: number): Promise<ContactItem> {
  return apiCall(`fetch contact ${contactId}`, () =>
    apiClient.get<ContactItem>(`/contacts/${contactId}`)
  );
}

/**
 * Create new contact
 *
 * @param data - Contact creation data
 * @returns Promise resolving to created contact
 *
 * @example
 * ```typescript
 * const newContact = await createContact({
 *   name: 'John Doe',
 *   first_name: 'John',
 *   last_name: 'Doe',
 *   email: 'john.doe@example.com',
 *   phone: '+1-555-1234',
 *   title: 'VP of Sales',
 *   organization_id: 123,
 *   address: '123 Main St, Houston, TX 77043'
 * });
 * ```
 */
export async function createContact(data: ContactCreateRequest): Promise<ContactItem> {
  return apiCall('create contact', () =>
    apiClient.post<ContactItem>('/contacts', data)
  );
}

/**
 * Update existing contact
 *
 * @param contactId - Contact ID
 * @param data - Contact update data (partial)
 * @returns Promise resolving to updated contact
 *
 * @example
 * ```typescript
 * const updated = await updateContact(456, {
 *   title: 'Senior VP of Sales',
 *   email: 'john.doe.new@example.com'
 * });
 * ```
 */
export async function updateContact(
  contactId: number,
  data: ContactUpdateRequest
): Promise<ContactItem> {
  return apiCall(`update contact ${contactId}`, () =>
    apiClient.put<ContactItem>(`/contacts/${contactId}`, data)
  );
}

/**
 * Delete contact
 *
 * @param contactId - Contact ID
 * @returns Promise resolving to success message
 *
 * @example
 * ```typescript
 * await deleteContact(456);
 * console.log('Contact deleted successfully');
 * ```
 */
export async function deleteContact(contactId: number): Promise<SuccessResponse> {
  return apiCall(`delete contact ${contactId}`, () =>
    apiClient.delete<SuccessResponse>(`/contacts/${contactId}`)
  );
}

/**
 * Fetch contacts by organization ID
 *
 * @param orgId - Organization ID
 * @returns Promise resolving to contacts list
 *
 * @example
 * ```typescript
 * const contacts = await fetchContactsByOrganization(123);
 * console.log(contacts.total); // Total contact count
 * ```
 */
export async function fetchContactsByOrganization(
  orgId: number
): Promise<ContactListResponse> {
  return apiCall(`fetch contacts for organization ${orgId}`, () =>
    apiClient.get<ContactListResponse>('/contacts', {
      params: { organization_id: orgId },
    })
  );
}

/**
 * Fetch hierarchical contact tree grouped by organization
 *
 * Used for Contact Management tree navigation view. Groups contacts by their
 * organizations and supports week/day filtering for route planning.
 *
 * @param campaignId - Optional campaign ID to filter by
 * @param week - Optional week number (1-12) to filter by
 * @param day - Optional day number (1-7) to filter by
 * @param showHidden - Optional flag to include hidden contacts (default: false)
 * @returns Promise resolving to tree structure
 *
 * @example
 * ```typescript
 * // Fetch all contacts grouped by organization
 * const tree = await fetchContactsTree();
 *
 * // Fetch contacts for specific campaign, week 2, day 3
 * const filteredTree = await fetchContactsTree('CAMPAIGN_123', 2, 3);
 *
 * // Fetch with hidden contacts included
 * const treeWithHidden = await fetchContactsTree('CAMPAIGN_123', undefined, undefined, true);
 * ```
 */
export async function fetchContactsTree(
  campaignId?: string,
  week?: number,
  day?: number,
  showHidden?: boolean
): Promise<OrganizationTreeNode[]> {
  const params: Record<string, string | number | boolean> = {};
  if (campaignId) params.campaign_id = campaignId;
  if (week !== undefined) params.week = week;
  if (day !== undefined) params.day = day;
  if (showHidden !== undefined) params.show_hidden = showHidden;

  return apiCall('fetch contacts tree', () =>
    apiClient.get<OrganizationTreeNode[]>('/contacts/contacts-tree', { params })
  );
}

/**
 * Toggle contact priority flag
 *
 * Marks a contact as high priority (starred) or removes priority flag.
 * Uses optimistic updates in the store for instant UI feedback.
 *
 * @param contactId - Contact ID
 * @returns Promise resolving to updated contact
 *
 * @example
 * ```typescript
 * // Toggle priority for contact
 * const updated = await toggleContactPriority(456);
 * console.log(updated.priority); // true or false
 * ```
 */
export async function toggleContactPriority(contactId: number): Promise<ContactItem> {
  return apiCall(`toggle priority for contact ${contactId}`, () =>
    apiClient.put<ContactItem>(`/contacts/${contactId}/priority`)
  );
}

/**
 * Hide contact from views
 *
 * Adds contact to global hidden contacts list. Hidden contacts will not appear
 * in contact lists, tree navigation, or label generation until unhidden.
 *
 * @param contactId - Contact ID
 * @returns Promise resolving to success response
 *
 * @example
 * ```typescript
 * // Hide a contact
 * await hideContactAPI(456);
 * console.log('Contact hidden successfully');
 * ```
 */
export async function hideContactAPI(contactId: number): Promise<SuccessResponse> {
  return apiCall(`hide contact ${contactId}`, () =>
    apiClient.post<SuccessResponse>(`/contacts/${contactId}/hide`)
  );
}

/**
 * Bulk hide multiple contacts
 *
 * Adds multiple contacts to global hidden contacts list in a single API call.
 * More efficient than hiding contacts one-by-one.
 *
 * @param contactIds - Array of contact IDs to hide
 * @returns Promise resolving to success response with count
 *
 * @example
 * ```typescript
 * // Hide multiple contacts at once
 * await hideContactsBulk([456, 789, 123]);
 * console.log('3 contacts hidden successfully');
 * ```
 */
export async function hideContactsBulk(contactIds: number[]): Promise<SuccessResponse> {
  return apiCall('bulk hide contacts', () =>
    apiClient.post<SuccessResponse>('/contacts/hide', {
      contact_ids: contactIds
    })
  );
}

/**
 * Batch update contact selections for a campaign
 *
 * Updates multiple contact selection states in a single API call.
 * Used for persisting contact batch selections to the backend.
 *
 * @param updates - Array of contact updates with contact_id and is_selected
 * @param campaignId - Campaign ID (required for selection updates)
 * @returns Promise resolving to batch update response
 *
 * @example
 * ```typescript
 * // Save contact selections for a campaign
 * const updates = [
 *   { contact_id: 123, is_selected: true },
 *   { contact_id: 456, is_selected: false }
 * ];
 * await batchUpdateContacts(updates, 'CAMPAIGN_20251122_120000');
 * ```
 */
export async function batchUpdateContacts(
  updates: Array<{ contact_id: number; is_selected: boolean }>,
  campaignId: string
): Promise<BatchUpdateResponse> {
  return apiCall('batch update contacts', () =>
    apiClient.put('/contacts/batch', {
      updates,
      campaign_id: campaignId
    })
  );
}

/**
 * Fetch saved contact selections for a campaign
 *
 * Retrieves the label_contact_selections from the PostgreSQL campaign table.
 * This provides persistent contact selections that survive browser refresh and work cross-device.
 *
 * @param campaignId - Campaign ID to fetch selections for
 * @returns Promise resolving to contact selections object
 *
 * @example
 * ```typescript
 * // Load saved selections for a campaign
 * const selections = await fetchContactSelections('CAMPAIGN_20251122_120000');
 * // Returns: { "123": [456, 789], "456": [101, 102] }
 * // Keys are org IDs (strings), values are arrays of selected contact IDs
 * ```
 */
export async function fetchContactSelections(
  campaignId: string
): Promise<{ label_contact_selections: Record<string, number[]> }> {
  return apiCall(`fetch contact selections for campaign ${campaignId}`, () =>
    apiClient.get<{ label_contact_selections: Record<string, number[]> }>(
      `/campaigns/${campaignId}/contact-selections`
    )
  );
}

/**
 * Fetch contact flags (priority and hidden) for a campaign
 *
 * Retrieves the contact_flags from the PostgreSQL campaign table.
 * This provides persistent flags that survive browser refresh and work cross-device.
 *
 * @param campaignId - Campaign ID to fetch flags for
 * @returns Promise resolving to contact flags object
 *
 * @example
 * ```typescript
 * // Load saved flags for a campaign
 * const flags = await fetchContactFlags('CAMPAIGN_20251122_120000');
 * // Returns: { priority: [123, 456], hidden: [789] }
 * ```
 */
export async function fetchContactFlags(
  campaignId: string
): Promise<{ campaign_id: string; priority: number[]; hidden: number[] }> {
  return apiCall(`fetch contact flags for campaign ${campaignId}`, () =>
    apiClient.get<{ campaign_id: string; priority: number[]; hidden: number[] }>(
      `/campaigns/${campaignId}/contact-flags`
    )
  );
}

/**
 * Update contact flag (priority or hidden) for a campaign
 *
 * Updates a single contact's flag in the PostgreSQL campaign table.
 *
 * @param campaignId - Campaign ID
 * @param contactId - Contact ID
 * @param flagType - Flag type: 'priority' or 'hidden'
 * @param enabled - Enable (true) or disable (false) the flag
 * @returns Promise resolving to updated flags object
 *
 * @example
 * ```typescript
 * // Add priority flag to a contact
 * await updateContactFlag('CAMPAIGN_20251122_120000', 456, 'priority', true);
 *
 * // Remove hidden flag from a contact
 * await updateContactFlag('CAMPAIGN_20251122_120000', 789, 'hidden', false);
 * ```
 */
export async function updateContactFlag(
  campaignId: string,
  contactId: number,
  flagType: 'priority' | 'hidden',
  enabled: boolean
): Promise<{ campaign_id: string; priority: number[]; hidden: number[] }> {
  return apiCall(`update contact flag ${flagType} for contact ${contactId}`, () =>
    apiClient.post<{ campaign_id: string; priority: number[]; hidden: number[] }>(
      `/campaigns/${campaignId}/contact-flags`,
      {
        contact_id: contactId,
        flag_type: flagType,
        enabled
      }
    )
  );
}
